import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  InputNumber,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';

const AdministracionPlanes = () => {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar planes
  const loadPlanes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/planes');
      if (response.ok) {
        const data = await response.json();
        setPlanes(data);
      } else {
        message.error('Error al cargar los planes');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los planes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanes();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nuevo plan
  const handleCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  };

  // Abrir modal para editar plan
  const handleEdit = (record) => {
    setEditingPlan(record);
    form.setFieldsValue({
      nombre: record.pla_nombre,
      valor: record.pla_valor,
      cantidadCreditos: record.pla_cantidad_creditos,
      diasValidez: record.pla_dias_validez,
      activo: record.pla_activo,
    });
    setModalVisible(true);
  };

  // Guardar plan (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingPlan) {
        // Actualizar
        const response = await apiClient.put(`/planes/${editingPlan.pla_id}`, values);
        if (response.ok) {
          message.success('Plan actualizado correctamente');
          setModalVisible(false);
          loadPlanes();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el plan');
        }
      } else {
        // Crear
        const response = await apiClient.post('/planes', values);
        if (response.ok) {
          message.success('Plan creado correctamente');
          setModalVisible(false);
          loadPlanes();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el plan');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al guardar el plan');
    }
  };

  // Eliminar plan
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/planes/${id}`);
      if (response.ok) {
        message.success('Plan eliminado correctamente');
        loadPlanes();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el plan');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el plan');
    }
  };

  // Formatear valor como moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value);
  };

  const normalizeText = (value = '') =>
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const isCreditoGratuito = (plan) => {
    const nombre = normalizeText(plan?.pla_nombre);
    return nombre === 'credito gratuito' || nombre.includes('credito gratuito');
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'pla_nombre',
      key: 'pla_nombre',
    },
    {
      title: 'Valor',
      dataIndex: 'pla_valor',
      key: 'pla_valor',
      width: 120,
      render: (valor) => formatCurrency(valor),
    },
    {
      title: 'Créditos',
      dataIndex: 'pla_cantidad_creditos',
      key: 'pla_cantidad_creditos',
      width: 100,
      align: 'center',
    },
    {
      title: 'Días de Validez',
      dataIndex: 'pla_dias_validez',
      key: 'pla_dias_validez',
      width: 130,
      align: 'center',
      render: (dias) => `${dias} días`,
    },
    {
      title: 'Estado',
      dataIndex: 'pla_activo',
      key: 'pla_activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Fecha de Creación',
      dataIndex: 'pla_fecha_creacion',
      key: 'pla_fecha_creacion',
      width: 180,
      render: (fecha) => fecha ? new Date(fecha).toLocaleString('es-AR') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          {!isCreditoGratuito(record) && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Editar
              </Button>
              <Popconfirm
                title="¿Estás seguro de eliminar este plan?"
                onConfirm={() => handleDelete(record.pla_id)}
                okText="Sí"
                cancelText="No"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                >
                  Eliminar
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Administración de Planes</h1>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadPlanes}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nuevo Plan
            </Button>
          </Space>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {planes.map(plan => (
              <Card
                key={plan.pla_id}
                hoverable
                style={{
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {plan.pla_nombre}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Valor: {formatCurrency(plan.pla_valor)}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Créditos: {plan.pla_cantidad_creditos}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Días de Validez: {plan.pla_dias_validez} días
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Fecha de Creación: {plan.pla_fecha_creacion ? new Date(plan.pla_fecha_creacion).toLocaleString('es-AR') : '-'}
                      </div>
                    </div>
                    <Tag color={plan.pla_activo ? 'green' : 'red'}>
                      {plan.pla_activo ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <Space wrap>
                    {!isCreditoGratuito(plan) && (
                      <>
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(plan)}
                        >
                          Editar
                        </Button>
                        <Popconfirm
                          title="¿Estás seguro de eliminar este plan?"
                          onConfirm={() => handleDelete(plan.pla_id)}
                          okText="Sí"
                          cancelText="No"
                        >
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                          >
                            Eliminar
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={planes}
            loading={loading}
            rowKey="pla_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} planes`,
            }}
          />
        )}

        {/* Modal para crear/editar */}
        <Modal
          title={editingPlan ? 'Editar Plan' : 'Nuevo Plan'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ej: Plan Básico, Plan Premium" />
            </Form.Item>

            <Form.Item
              name="valor"
              label="Valor"
              rules={[
                { required: true, message: 'El valor es requerido' },
                { type: 'number', min: 0, message: 'El valor debe ser mayor o igual a 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item>

            <Form.Item
              name="cantidadCreditos"
              label="Cantidad de Créditos"
              rules={[
                { required: true, message: 'La cantidad de créditos es requerida' },
                { type: 'number', min: 0, message: 'La cantidad debe ser mayor o igual a 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0"
                min={0}
                step={1}
              />
            </Form.Item>

            <Form.Item
              name="diasValidez"
              label="Días de Validez"
              rules={[
                { required: true, message: 'Los días de validez son requeridos' },
                { type: 'number', min: 1, message: 'Los días deben ser mayor a 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Ej: 30, 60, 90"
                min={1}
                step={1}
                addonAfter="días"
              />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionPlanes;


